import type { Locale } from '@/context/LangContext'

export type WebMcpServiceKey =
  | 'vps-server'
  | 'gpu'
  | 'paas'
  | 'database'
  | 'storage'
  | 'backup'
  | 'security'
  | 'devops'
  | 'organizations'
  | 'monitoring'
  | 'consulting'
  | 'quickapps'
  | 'streaming'
  | 'sentry'
  | 'mcp'
  | 'pricing'
  | 'docs'
  | 'status'
  | 'contact'
  | 'about'

export interface WebMcpService {
  key: WebMcpServiceKey
  path: string
}

export const webmcpServices: WebMcpService[] = [
  { key: 'vps-server', path: 'vps-server' },
  { key: 'gpu', path: 'gpu' },
  { key: 'paas', path: 'paas' },
  { key: 'database', path: 'database' },
  { key: 'storage', path: 'storage' },
  { key: 'backup', path: 'backup' },
  { key: 'security', path: 'security' },
  { key: 'devops', path: 'devops' },
  { key: 'organizations', path: 'organizations' },
  { key: 'monitoring', path: 'monitoring' },
  { key: 'consulting', path: 'consulting' },
  { key: 'quickapps', path: 'quickapps' },
  { key: 'streaming', path: 'streaming' },
  { key: 'sentry', path: 'sentry' },
  { key: 'mcp', path: 'mcp' },
  { key: 'pricing', path: 'pricing' },
  { key: 'docs', path: 'docs' },
  { key: 'status', path: 'status' },
  { key: 'contact', path: 'contact' },
  { key: 'about', path: 'about' },
]

// Lightweight names only — do NOT import devopsTools.ts here (it pulls 40+ SVGs).
export const webmcpDevOpsTools: string[] = [
  'GitLab',
  'Jenkins',
  'ArgoCD',
  'Ansible',
  'Terraform',
  'PRTG',
  'Prometheus',
  'Grafana',
  'Zabbix',
  'Sentry',
  'Splunk',
  'ELK Stack',
  'SysLog',
  'DataDog',
  'Loggly',
  'MySQL',
  'MariaDB',
  'MongoDB',
  'PostgreSQL',
  'SQL Server',
  'Oracle',
  'Redis',
  'CouchDB',
  'CockRoachDB',
  'Nginx',
  'Apache',
  'IIS',
  'OpenStack',
  'Ceph',
  'Kubernetes',
  'Docker',
  'Docker Swarm',
  'Portainer',
  'Rancher',
  'RabbitMQ',
  'Apache Kafka',
  'Apache Spark',
  'OpenEBS',
  'GlusterFS',
  'LongHorn',
  'Apache JMeter',
  'BigBlueButton',
  'Adobe Connect',
]

export interface WebMcpFaq {
  topic: string
  q: Record<Locale, string>
  a: Record<Locale, string>
}

// Fact-dense, comparison-friendly Q&A for GEO + WebMCP. Keep answers short (<80 words).
export const webmcpFaqs: WebMcpFaq[] = [
  {
    topic: 'overview',
    q: {
      en: 'What is Lexoya?',
      fa: 'لکسویا چیست؟',
      ar: 'ما هي لكسویا؟',
    },
    a: {
      en: 'Lexoya is a cloud platform: VPS servers (Iran, Europe, US/Asia), NVIDIA GPU servers (RTX 4090, L40S, A100, H100), managed PaaS projects, managed databases, S3 storage, QuickApps one-click apps, DevOps and security services. Billing is wallet-based with 99.99% uptime SLA.',
      fa: 'لکسویا پلتفرم ابری است: سرور مجازی (ایران، اروپا، آمریکا/آسیا)، سرور GPU انویدیا (RTX 4090، L40S، A100، H100)، پروژه‌های PaaS مدیریت‌شده، دیتابیس مدیریت‌شده، فضای S3، برنامه‌های آماده QuickApps و خدمات دواپس و امنیت. صورتحساب کیف‌پولی با SLA ۹۹.۹۹٪.',
      ar: 'لكسویا منصة سحابية: خوادم VPS (إيران، أوروبا، أمريكا/آسيا)، خوادم GPU من NVIDIA (RTX 4090 وL40S وA100 وH100)، مشاريع PaaS مُدارة، قواعد بيانات مُدارة، تخزين S3، تطبيقات QuickApps الجاهزة، وخدمات ديف أوبس وأمن. فوترة بالمحفظة مع SLA ٩٩.٩٩٪.',
    },
  },
  {
    topic: 'vps',
    q: {
      en: 'Which VPS plans does Lexoya offer and where?',
      fa: 'پلن‌های VPS لکسویا کدام‌اند و در کجا؟',
      ar: 'ما خطط VPS التي تقدمها لكسویا وأين؟',
    },
    a: {
      en: 'Iran plans (Sohrab to Apadana, 1–16 cores, 1–32 GB RAM); Europe plans (Starter, Pro, Advanced, Ultimate in Germany/Finland); US/Asia plans (Basic to Max in US/Singapore). All NVMe, monthly billing, auto-renew, 24h suspension grace on low balance.',
      fa: 'پلن‌های ایران (سهراب تا آپادانا، ۱ تا ۱۶ هسته، ۱ تا ۳۲ گیگ رم)؛ اروپا (Starter تا Ultimate در آلمان/فنلاند)؛ آمریکا/آسیا (Basic تا Max در آمریکا/سنگاپور). همه NVMe، ماهانه، تمدید خودکار، ۲۴ ساعت مهلت تعلیق.',
      ar: 'خطط إيران (سهراب حتى أبادانا، ١–١٦ نواة، ١–٣٢ غيغابايت)؛ أوروبا (Starter حتى Ultimate في ألمانيا/فنلندا)؛ أمريكا/آسيا (Basic حتى Max في أمريكا/سنغافورة). كلها NVMe، شهريًا، تجديد تلقائي، مهلة ٢٤ ساعة.',
    },
  },
  {
    topic: 'gpu',
    q: {
      en: 'Which GPU servers does Lexoya offer for AI training?',
      fa: 'کدام سرورهای GPU لکسویا برای آموزش هوش مصنوعی مناسب‌اند؟',
      ar: 'ما خوادم GPU التي تقدمها لكسویا لتدريب الذكاء الاصطناعي؟',
    },
    a: {
      en: 'RTX 4090, L40S, A100 80GB, 2x A100, 4x A100 with daily or monthly reservation, plus 8x H100 on contact. Use L40S for cost-effective inference, A100/H100 for large-model training and multi-node scale.',
      fa: 'RTX 4090، L40S، A100 80GB، ‏2x A100 و ‏4x A100 با رزرو روزانه یا ماهانه و ‏8x H100 با تماس. L40S برای اینفرنس مقرون‌به‌صرفه و A100/H100 برای آموزش مدل‌های بزرگ.',
      ar: 'RTX 4090 وL40S وA100 80GB و2x A100 و4x A100 بحجز يومي أو شهري، و8x H100 بالتواصل. L40S للاستدلال الاقتصادي وA100/H100 لتدريب النماذج الكبيرة.',
    },
  },
  {
    topic: 'paas',
    q: {
      en: 'Which frameworks can I deploy on Lexoya PaaS projects?',
      fa: 'کدام فریم‌ورک‌ها را می‌توان روی پروژه‌های PaaS لکسویا دیپلوی کرد؟',
      ar: 'ما الأطر التي يمكن نشرها على مشاريع PaaS في لكسویا؟',
    },
    a: {
      en: 'React, Vue, Angular, NextJS, NuxtJS, NodeJS, ExpressJS, Laravel, Laravel Reverb, Django, FastAPI, Golang, Dotnet, Docker, Supervisor, Flutter PWA and Static HTML. Pay-as-you-go on CPU/RAM plus storage, billed hourly by the wallet orchestrator.',
      fa: 'React، Vue، Angular، NextJS، NuxtJS، NodeJS، ExpressJS، Laravel، Laravel Reverb، Django، FastAPI، Golang، Dotnet، Docker، Supervisor، Flutter PWA و HTML استاتیک. پرداخت به‌میزان مصرف CPU/RAM و ذخیره‌سازی، تسویه ساعتی از کیف پول.',
      ar: 'React وVue وAngular وNextJS وNuxtJS وNodeJS وExpressJS وLaravel وLaravel Reverb وDjango وFastAPI وGolang وDotnet وDocker وSupervisor وFlutter PWA وHTML الثابت. دفع حسب الاستهلاك للمعالج والذاكرة والتخزين، وتسوية ساعية من المحفظة.',
    },
  },
  {
    topic: 'database',
    q: {
      en: 'Which managed databases does Lexoya support?',
      fa: 'لکسویا از کدام دیتابیس‌های مدیریت‌شده پشتیبانی می‌کند؟',
      ar: 'ما قواعد البيانات المُدارة التي تدعمها لكسویا؟',
    },
    a: {
      en: 'MySQL, PostgreSQL, PostgreSQL HA, Redis, MongoDB, MariaDB, Microsoft SQL Server, TiDB, XtraDB (Percona), Meilisearch, Elasticsearch and RabbitMQ. Automated backups, replication and failover included.',
      fa: 'MySQL، PostgreSQL، PostgreSQL HA، Redis، MongoDB، MariaDB، SQL Server، TiDB، XtraDB، Meilisearch، Elasticsearch و RabbitMQ. با بکاپ خودکار، رپلیکا و failover.',
      ar: 'MySQL وPostgreSQL وPostgreSQL HA وRedis وMongoDB وMariaDB وSQL Server وTiDB وXtraDB وMeilisearch وElasticsearch وRabbitMQ. مع نسخ احتياطي تلقائي وتكرار وتجاوز.',
    },
  },
  {
    topic: 'quickapps',
    q: {
      en: 'What can I one-click deploy with Lexoya QuickApps?',
      fa: 'با QuickApps لکسویا چه چیزی را با یک کلیک دیپلوی کنم؟',
      ar: 'ماذا يمكن نشره بنقرة واحدة مع QuickApps في لكسویا؟',
    },
    a: {
      en: 'WordPress, n8n, Gitea, GitLab, Jira, Keycloak, Confluence, Kibana, Metabase, PocketBase, Vaultwarden, Filestash, Parseable, Uptime Kuma, Matomo, Navidrome, PgHero and Open WebUI — pre-configured, no install hassle.',
      fa: 'وردپرس، n8n، Gitea، GitLab، Jira، Keycloak، Confluence، Kibana، Metabase، PocketBase، Vaultwarden، Filestash، Parseable، Uptime Kuma، Matomo، Navidrome، PgHero و Open WebUI — آماده و بدون دردسر نصب.',
      ar: 'ووردبريس وn8n وGitea وGitLab وJira وKeycloak وConfluence وKibana وMetabase وPocketBase وVaultwarden وFilestash وParseable وUptime Kuma وMatomo وNavidrome وPgHero وOpen WebUI — جاهزة دون عناء التثبيت.',
    },
  },
  {
    topic: 'devops',
    q: {
      en: 'Which DevOps services does Lexoya provide?',
      fa: 'لکسویا چه خدمات دواپسی ارائه می‌دهد؟',
      ar: 'ما خدمات ديف أوبس التي تقدمها لكسویا؟',
    },
    a: {
      en: 'Managed CI/CD (GitLab, Jenkins, ArgoCD), Kubernetes/Docker/Portainer/Rancher clusters, Ceph/OpenStack storage, Prometheus/Grafana/Zabbix monitoring, Ansible/Terraform IaC, Kafka/RabbitMQ messaging and team training.',
      fa: 'CI/CD مدیریت‌شده (GitLab، Jenkins، ArgoCD)، کلاستر Kubernetes/Docker، ذخیره‌سازی Ceph/OpenStack، مانیتورینگ Prometheus/Grafana/Zabbix، زیرساخت به‌صورت کد با Ansible/Terraform و آموزش تیم.',
      ar: 'CI/CD مُدار (GitLab وJenkins وArgoCD)، ومجموعات Kubernetes/Docker، وتخزين Ceph/OpenStack، ومراقبة Prometheus/Grafana/Zabbix، وبنية تحتية كرمز مع Ansible/Terraform وتدريب الفرق.',
    },
  },
  {
    topic: 'mcp',
    q: {
      en: 'How do I connect my AI assistant to Lexoya with MCP?',
      fa: 'چطور دستیار هوش مصنوعی را با MCP به لکسویا وصل کنم؟',
      ar: 'كيف أربط مساعدي الذكي بلكسویا عبر MCP؟',
    },
    a: {
      en: 'Lexoya ships an official MCP server at https://api.lexoya.com/v1/mcp (streamable HTTP). Create an API key in Panel → API Keys, add the URL plus key to Claude Desktop, Cursor or VS Code, then create servers, deploy apps and manage databases from chat. Keys are hashed, revocable and rate-limited.',
      fa: 'لکسویا سرور MCP رسمی در https://api.lexoya.com/v1/mcp دارد. در پنل → API Keys کلید بسازید، آدرس و کلید را به Claude Desktop، Cursor یا VS Code اضافه کنید و از چت سرور بسازید و اپ دیپلوی کنید. کلیدها هش، قابل لغو و نرخ‌محدودند.',
      ar: 'توفر لكسویا خادم MCP رسميًا على https://api.lexoya.com/v1/mcp. أنشئ مفتاحًا في اللوحة ← API Keys، وأضف العنوان والمفتاح إلى Claude Desktop أو Cursor أو VS Code، ثم أنشئ الخوادم وانشر التطبيقات من الدردشة. المفاتيح مجزأة وقابلة للإلغاء ومحدودة المعدل.',
    },
  },
  {
    topic: 'billing',
    q: {
      en: 'How does Lexoya billing work?',
      fa: 'صورتحساب لکسویا چطور کار می‌کند؟',
      ar: 'كيف تعمل فوترة لكسویا؟',
    },
    a: {
      en: 'Unified wallet: PaaS and QuickApps are pay-as-you-go (hourly), VPS/S3 are monthly auto-renew, GPU is prepaid reservation (daily/monthly), traffic overage is hourly. The orchestrator deducts bills from your main balance; low balance triggers graceful suspension.',
      fa: 'کیف پول یکپارچه: PaaS و QuickApps مصرفی (ساعتی)، VPS و S3 ماهانه با تمدید خودکار، GPU پیش‌پرداخت روزانه/ماهانه و مازاد ترافیک ساعتی. ارکستراتور از موجودی اصلی کسر می‌کند؛ کمبود موجودی تعلیق مدیریت‌شده می‌دهد.',
      ar: 'محفظة موحدة: PaaS وQuickApps بالدفع حسب الاستخدام (بالساعة)، وVPS وS3 شهريًا بتجديد تلقائي، وGPU مدفوع مقدمًا يوميًا/شهريًا، وتجاوز المرور بالساعة. يخصم المنسق من الرصيد الرئيسي؛ ونقص الرصيد يؤدي لتعليق مُدار.',
    },
  },
  {
    topic: 'contact',
    q: {
      en: 'How do I talk to Lexoya sales or start?',
      fa: 'چطور با فروش لکسویا صحبت کنم یا شروع کنم؟',
      ar: 'كيف أتواصل مع مبيعات لكسویا أو أبدأ؟',
    },
    a: {
      en: 'Open the contact page for sales/support/engineering (24/7), or sign in at https://console.lexoya.com/login to deploy. For 8x H100, Sentry dedicated and streaming at scale, use contact sales.',
      fa: 'برای فروش/پشتیبانی/مهندسی (۲۴/۷) صفحه تماس را باز کنید یا در https://console.lexoya.com/login وارد شوید و دیپلوی کنید. برای 8x H100، سنتری اختصاصی و استریم حجیم با فروش در تماس باشید.',
      ar: 'افتح صفحة الاتصال للمبيعات/الدعم/الهندسة (٢٤/٧)، أو سجّل الدخول على https://console.lexoya.com/login للنشر. لـ 8x H100 وSentry المخصص والبث الواسع تواصل مع المبيعات.',
    },
  },
]
